import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Drawer, Popover } from "@mantine/core";
import moment from "moment";
import Icon from "./icons";
import WeekPicker from "./WeekPicker";
import { addAttendanceStaff } from "../../../services/UserTracking";
import { getAllPatientsModal } from "../../../services/patient";
import "./SchedulerCreate.css";

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);
const clientNameOf = (c) =>
  c?.form1 ? `${c.form1.firstName || ""} ${c.form1.lastName || ""}`.trim() || c?.name || "Client" : c?.name || "Client";

// Custom dropdown matching the rest of the module (cdd chrome).
function Cdd({ value, options, onChange, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const sel = options.find((o) => o.value === value);
  return (
    <div className={`cdd ${open ? "open" : ""}`} ref={ref}>
      <button type="button" className="cdd-btn" onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen((o) => !o); }}>
        {sel ? <span>{sel.label}</span> : <span className="cdd-ph">{placeholder}</span>}
        <Icon name="chevronDown" size={16} />
      </button>
      <div className="cdd-menu">
        {options.length === 0 ? (
          <div className="cdd-opt" style={{ color: "var(--app-text-muted)", cursor: "default" }}><div className="co-name">No options</div></div>
        ) : options.map((o) => (
          <div key={o.value} className={`cdd-opt ${o.value === value ? "sel" : ""}`} onClick={() => { onChange(o.value); setOpen(false); }}>
            <div className="co-name">{o.label}</div>
            <span className="co-chk"><Icon name="check" size={15} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Staff manual time entry: pick the Day (admin date picker, today/past only) and
// the Facility. When the facility's clock-in type is "client", the staff also
// picks one of their assigned Clients and that client's Service. Saved via
// addAttendanceStaff.
export default function StaffTimeEntryDrawer({ opened, onClose, onSaved, staffId, staffName, weekLabel }) {
  const [date, setDate] = useState(new Date());
  const [datePop, setDatePop] = useState(false);
  const [dpMonth, setDpMonth] = useState(new Date());
  const [facilityId, setFacilityId] = useState("");
  const [clientId, setClientId] = useState("");
  const [service, setService] = useState("");
  const [clockIn, setClockIn] = useState("09:00");
  const [clockOut, setClockOut] = useState("17:00");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { allPatients } = useSelector((state) => state?.patient) || {};
  const { profile } = useSelector((state) => state?.profile) || {};

  // All facilities the staff is assigned to (same source the clock-in modal
  // uses), excluding deleted and no-clockin facilities. Both facility- and
  // client-type are shown; the drawer adapts based on the chosen facility.
  const facilities = useMemo(() => {
    return (profile?.user?.facilityId || []).filter(
      (f) => f && f.deleted === false && f.checkInType !== "no-clockin"
    );
  }, [profile]);
  const selFac = facilities.find((f) => idOf(f) === facilityId);
  const isClientType = selFac?.checkInType === "client";

  // Active clients assigned to the selected (client clock-in) facility, from
  // the patient store — not limited to this week's shifts.
  const clients = useMemo(() => {
    if (!facilityId) return [];
    return (allPatients || []).filter(
      (p) =>
        p && p.deleted === false && p.active === true &&
        (p.facilityId || []).some((fRef) => {
          const fId = typeof fRef === "string" ? fRef : (fRef?.id || fRef?._id);
          return fId === facilityId;
        })
    );
  }, [allPatients, facilityId]);
  // Services live on the full patient record (form1.services); the selected
  // client is already a full patient object from allPatients.
  const fullClient = allPatients?.find((p) => idOf(p) === clientId);
  const serviceOpts = useMemo(() => {
    const list = fullClient?.form1?.services || [];
    return list.filter((s) => s && s.servicetitle).map((s) => ({ value: s.servicenumber || s.servicetitle, label: s.servicenumber ? `${s.servicetitle} (${s.servicenumber})` : s.servicetitle }));
  }, [fullClient]);

  // Ensure the patient list is loaded (clients/services come from it).
  useEffect(() => {
    if (opened && !allPatients?.length) getAllPatientsModal("all", false);
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!opened) return;
    setDate(new Date());
    setDpMonth(new Date());
    setDatePop(false);
    setFacilityId(facilities.length === 1 ? idOf(facilities[0]) : "");
    setClientId("");
    setService("");
    setClockIn("09:00");
    setClockOut("17:00");
    setNote("");
    setErr("");
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!date) { setErr("Pick a day."); return; }
    if (!facilityId) { setErr("Select a facility."); return; }
    if (isClientType && !clientId) { setErr("Select a client."); return; }
    const [ih, im] = clockIn.split(":").map(Number);
    const [oh, om] = clockOut.split(":").map(Number);
    const base = new Date(date);
    const start = new Date(base); start.setHours(ih, im, 0, 0);
    const end = new Date(base); end.setHours(oh, om, 0, 0);
    // Overnight shift: a clock-out at/before the clock-in time means it crosses
    // midnight, so roll the end to the next day.
    if (end <= start) end.setDate(end.getDate() + 1);
    const d0 = new Date(base); d0.setHours(0, 0, 0, 0);
    const payload = {
      staff: staffId,
      facility: facilityId,
      checkin_type: isClientType ? "client" : "facility",
      date: d0,
      startTime: start,
      endTime: end,
      status: "clock-out",
      breaks: [],
    };
    if (isClientType && clientId) { payload.client = clientId; payload.service = service || ""; }
    setBusy(true);
    const res = await addAttendanceStaff(payload);
    setBusy(false);
    if (res) { onSaved?.(); onClose?.(); }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={560}
      padding={0}
      withCloseButton={false}
      title={null}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      zIndex={1000}
    >
      <div className="sd-root">
        <div className="sd-head">
          <div>
            <h3>Add Time Entry</h3>
            <div className="dh-sub">{[staffName, weekLabel].filter(Boolean).join(" · ")}</div>
          </div>
          <button type="button" className="sd-close" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className="sd-body sd-create">
          <div className="field">
            <label>Day <span className="req">*</span></label>
            <Popover opened={datePop} onChange={setDatePop} position="bottom-start" withinPortal shadow="md" radius={16}>
              <Popover.Target>
                <button type="button" className="cdd-btn" onClick={() => setDatePop((o) => !o)}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Icon name="calendar" size={16} />
                    {date ? moment(date).format("ddd, MMM D, YYYY") : <span className="cdd-ph">Select a day</span>}
                  </span>
                  <Icon name="chevronDown" size={16} />
                </button>
              </Popover.Target>
              <Popover.Dropdown p={0}>
                <WeekPicker
                  mode="day"
                  monthDate={dpMonth}
                  selDay={date}
                  maxDate={new Date()}
                  onMonthChange={setDpMonth}
                  onPick={(d) => { setDate(d); setDpMonth(new Date(d.getFullYear(), d.getMonth(), 1)); setDatePop(false); }}
                  onClose={() => setDatePop(false)}
                />
              </Popover.Dropdown>
            </Popover>
          </div>

          <div className="field">
            <label>Facility <span className="req">*</span></label>
            {facilities.length ? (
              <Cdd
                value={facilityId}
                options={facilities.map((f) => ({ value: idOf(f), label: f.name }))}
                onChange={(v) => { setFacilityId(v); setClientId(""); setService(""); }}
                placeholder="Select a facility"
              />
            ) : (
              <div className="ro-field" style={{ color: "var(--app-text-muted)" }}>No facilities assigned</div>
            )}
          </div>

          {isClientType && (
            <div className="frow">
              <div className="field">
                <label>Client <span className="req">*</span></label>
                <Cdd value={clientId} options={clients.map((c) => ({ value: idOf(c), label: clientNameOf(c) }))} onChange={(v) => { setClientId(v); setService(""); }} placeholder="Select a client" />
              </div>
              <div className="field">
                <label>Service</label>
                <Cdd value={service} options={serviceOpts} onChange={setService} placeholder={serviceOpts.length ? "Select a service" : "No services"} disabled={!clientId} />
              </div>
            </div>
          )}

          <div className="frow">
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Clock in <span className="req">*</span></label>
              <input className="inp" type="time" value={clockIn} onChange={(e) => setClockIn(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Clock out <span className="req">*</span></label>
              <input className="inp" type="time" value={clockOut} onChange={(e) => setClockOut(e.target.value)} />
            </div>
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label>Note</label>
            <textarea className="inp" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain the change for your supervisor..." />
          </div>

          {err ? <div style={{ color: "#dc2626", fontSize: 12.5, marginBottom: 10 }}>{err}</div> : null}

          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--app-text-muted)" }}>
            <Icon name="pin" size={15} /> Manual entries are flagged for supervisor review.
          </div>
        </div>

        <div className="sd-foot">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <span className="spacer" />
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={!date || !facilityId || (isClientType && !clientId) || busy}>
            <Icon name="check" size={16} /> Add Entry
          </button>
        </div>
      </div>
    </Drawer>
  );
}
